import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Send, Copy } from "lucide-react";

const splitName = (full = "") => {
  const parts = full.trim().split(/\s+/);
  return { first_name: parts[0] || "", last_name: parts.slice(1).join(" ") };
};

export const copyLink = async (link) => {
  try { await navigator.clipboard.writeText(link); toast.success("Lien d'invitation copié"); }
  catch { window.prompt("Copiez le lien d'invitation :", link); }
};

export default function InviteDialog({ open, onOpenChange, prefill, onSent }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setForm({ ...splitName(prefill?.name), email: prefill?.email || "", message: prefill?.message || "" });
  }, [open, prefill]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/admin/invitations", { ...form, site_url: window.location.origin });
      setResult(data);
      if (data.email_sent) toast.success(`Invitation envoyée à ${form.email}`);
      else toast.warning("Invitation créée, mais l'email n'a pas pu partir. Copiez le lien pour l'envoyer vous-même.");
      onSent?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setSending(false);
  };

  const field = (k, label, props = {}) => (
    <div>
      <Label className="text-xs uppercase tracking-wider text-[#475569]">{label}</Label>
      <Input data-testid={`invite-${k.replace("_", "-")}`} value={form[k]} onChange={set(k)} className="rounded-none mt-1.5 border-gray-300" required {...props} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-lg" data-testid="invite-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#1B2A4A]">Inviter un cédant</DialogTitle>
          <DialogDescription>Le contact reçoit un email personnalisé pour créer son espace cédant et compléter la fiche de son entreprise.</DialogDescription>
        </DialogHeader>
        {result ? (
          <div data-testid="invite-result" className="space-y-4">
            <p className="text-sm text-[#1B2A4A]">{result.email_sent ? <>Email envoyé à <strong>{result.invitation.email}</strong>.</> : <>L'email n'a pas pu être envoyé ({(result.email_error || "").replace(/\.$/, "")}). Transmettez ce lien au contact :</>}</p>
            <div className="flex gap-2">
              <Input readOnly value={result.link} className="rounded-none border-gray-300 text-xs" data-testid="invite-link" />
              <button type="button" data-testid="invite-copy" onClick={() => copyLink(result.link)} className="px-3 border border-gray-300 hover:border-[#1B2A4A]"><Copy className="w-4 h-4" /></button>
            </div>
            <button type="button" onClick={() => onOpenChange(false)} className="text-sm px-5 py-2 bg-[#1B2A4A] text-white">Fermer</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">{field("first_name", "Prénom")}{field("last_name", "Nom")}</div>
            {field("email", "Email", { type: "email" })}
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#475569]">Message personnel (facultatif)</Label>
              <Textarea data-testid="invite-message" value={form.message} onChange={set("message")} rows={3} placeholder="Ex. : Ravi de notre échange de mardi, voici votre accès…" className="rounded-none mt-1.5 border-gray-300" />
            </div>
            <button type="submit" data-testid="invite-submit" disabled={sending} className="inline-flex items-center gap-2 bg-[#E0C173] hover:bg-[#C9A85A] text-[#1B2A4A] font-semibold px-6 py-2.5 rounded-full transition-colors disabled:opacity-50">
              <Send className="w-4 h-4" /> {sending ? "Envoi…" : "Envoyer l'invitation"}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
